import { useState } from "react";
import { toast } from "sonner";
import { CalendarPlus, Copy, Check, RefreshCw, Smartphone, Mail, ExternalLink } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { ConfirmDeleteDialog } from "@/components/admin/ConfirmDeleteDialog";

const GoogleIcon = (props) => (
  <svg viewBox="0 0 24 24" width="14" height="14" {...props}><path fill="currentColor" d="M21.6 12.2c0-.7-.1-1.4-.2-2H12v3.9h5.4a4.6 4.6 0 0 1-2 3v2.5h3.2c1.9-1.7 3-4.3 3-7.4Z" opacity=".9"/><path fill="currentColor" d="M12 22c2.7 0 5-.9 6.6-2.4l-3.2-2.5c-.9.6-2 1-3.4 1-2.6 0-4.8-1.8-5.6-4.1H3.1v2.6A10 10 0 0 0 12 22Z" opacity=".7"/><path fill="currentColor" d="M6.4 14a6 6 0 0 1 0-3.9V7.5H3.1a10 10 0 0 0 0 9l3.3-2.5Z" opacity=".5"/><path fill="currentColor" d="M12 6c1.5 0 2.8.5 3.8 1.5l2.8-2.8A10 10 0 0 0 3.1 7.5l3.3 2.6C7.2 7.8 9.4 6 12 6Z" opacity=".8"/></svg>
);

const GUIDES = [
  { key: "google", label: "Google Calendar", icon: GoogleIcon, steps: [
    "On a computer, open calendar.google.com (subscriptions can't be added from the phone app, but they'll sync to it).",
    "In the left sidebar next to \"Other calendars\", click the + and choose \"From URL\".",
    "Paste your private link below and click \"Add calendar\".",
    "Google refreshes subscribed calendars every 12–24 hours — new bookings will appear by the next day (the email attachment adds them instantly).",
  ]},
  { key: "apple", label: "Apple / iPhone", icon: Smartphone, steps: [
    "On your iPhone or iPad, tap the \"Subscribe on iPhone\" button below and choose \"Subscribe\".",
    "Or go to Settings → Calendar → Accounts → Add Account → Other → Add Subscribed Calendar and paste the link.",
    "On a Mac: Calendar → File → New Calendar Subscription, paste the link, set Auto-refresh to \"Every hour\".",
    "Your bookings will appear as a separate calendar you can colour and show or hide.",
  ]},
  { key: "outlook", label: "Outlook", icon: Mail, steps: [
    "Outlook.com / Microsoft 365: open Calendar → \"Add calendar\" → \"Subscribe from web\".",
    "Paste your private link, give it a name like \"O.R.E.C bookings\", and click Import.",
    "Outlook desktop: Home → Add Calendar → From Internet, then paste the link.",
    "Outlook checks for updates every few hours — the email attachment adds a new booking right away.",
  ]},
];

const CopyButton = ({ value, testid }) => {
  const [done, setDone] = useState(false);
  const copy = async () => {
    try { await navigator.clipboard.writeText(value); setDone(true); setTimeout(() => setDone(false), 1500); toast.success("Link copied"); }
    catch { toast.error("Couldn't copy — select the link and copy it manually"); }
  };
  return (
    <Button size="sm" variant="outline" className="rounded-full shrink-0" onClick={copy} data-testid={testid}>
      {done ? <Check size={13} className="mr-1.5" /> : <Copy size={13} className="mr-1.5" />}{done ? "Copied" : "Copy"}
    </Button>
  );
};

export const CalendarFeedCard = ({ profile }) => {
  const [feed, setFeed] = useState(null);
  const [guide, setGuide] = useState("google");
  const [busy, setBusy] = useState(false);
  const [confirmReset, setConfirmReset] = useState(false);

  if (!profile) return null;

  const open = async () => {
    setBusy(true);
    try { setFeed((await api.get("/calendar/feed")).data); } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const reset = async () => {
    setBusy(true);
    try {
      setFeed((await api.post("/calendar/feed/reset")).data);
      toast.success("New private link created — the old one no longer works");
      setConfirmReset(false);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const active = GUIDES.find((g) => g.key === guide);

  return (
    <div className="silk-card rounded-lg p-5 mb-6" data-testid="calendar-feed-card">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className="text-foreground text-sm font-medium flex items-center gap-2"><CalendarPlus size={15} className="text-primary" /> Your bookings in your own calendar</p>
          <p className="text-xs text-muted-foreground mt-1">Subscribe once and every confirmed session shows up in Google Calendar, Apple Calendar or Outlook — and updates itself if a seeker reschedules.</p>
        </div>
        {!feed && (
          <Button onClick={open} disabled={busy} className="rounded-full px-5" data-testid="calendar-feed-open-btn">
            <CalendarPlus size={14} className="mr-2" /> Add bookings to my calendar
          </Button>
        )}
      </div>

      {feed && (
        <div className="mt-5 space-y-5" data-testid="calendar-feed-panel">
          <div>
            <p className="text-[11px] uppercase tracking-wider text-muted-foreground mb-2">Your private calendar link</p>
            <div className="flex items-center gap-2">
              <code className="flex-1 min-w-0 truncate text-xs bg-background/60 border border-border rounded-full px-4 py-2 text-foreground" data-testid="calendar-feed-url">{feed.url}</code>
              <CopyButton value={feed.url} testid="calendar-feed-copy-btn" />
            </div>
            <p className="text-[11px] text-muted-foreground mt-2">Keep it private — anyone with this link can see your bookings. If it ever leaks, reset it below.</p>
          </div>

          <div className="flex flex-wrap gap-2" data-testid="calendar-feed-guides">
            {GUIDES.map((g) => (
              <button key={g.key} onClick={() => setGuide(g.key)} data-testid={`calendar-guide-${g.key}`}
                className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs transition-colors ${guide === g.key ? "border-primary text-primary" : "border-border text-muted-foreground hover:text-foreground"}`}>
                <g.icon /> {g.label}
              </button>
            ))}
          </div>
          <ol className="space-y-2" data-testid={`calendar-guide-steps-${guide}`}>
            {active.steps.map((s, i) => (
              <li key={i} className="flex gap-3 text-xs text-muted-foreground leading-relaxed">
                <span className="w-5 h-5 rounded-full border border-primary/50 text-primary flex items-center justify-center text-[10px] shrink-0">{i + 1}</span>
                <span>{s}</span>
              </li>
            ))}
          </ol>

          <div className="flex flex-wrap gap-2">
            {guide === "apple" && (
              <Button asChild size="sm" className="rounded-full" data-testid="calendar-feed-webcal-btn">
                <a href={feed.webcal_url}><Smartphone size={13} className="mr-1.5" /> Subscribe on iPhone / Mac</a>
              </Button>
            )}
            {guide === "google" && (
              <Button asChild size="sm" variant="outline" className="rounded-full" data-testid="calendar-feed-google-btn">
                <a href="https://calendar.google.com/calendar/u/0/r/settings/addbyurl" target="_blank" rel="noreferrer"><ExternalLink size={13} className="mr-1.5" /> Open Google Calendar "From URL"</a>
              </Button>
            )}
            {guide === "outlook" && (
              <Button asChild size="sm" variant="outline" className="rounded-full" data-testid="calendar-feed-outlook-btn">
                <a href="https://outlook.live.com/calendar/0/addcalendar" target="_blank" rel="noreferrer"><ExternalLink size={13} className="mr-1.5" /> Open Outlook "Add calendar"</a>
              </Button>
            )}
            <Button size="sm" variant="ghost" className="rounded-full text-muted-foreground" onClick={() => setConfirmReset(true)} disabled={busy} data-testid="calendar-feed-reset-btn">
              <RefreshCw size={13} className="mr-1.5" /> Reset link
            </Button>
          </div>
        </div>
      )}

      <ConfirmDeleteDialog open={confirmReset} onOpenChange={setConfirmReset} busy={busy} onConfirm={reset} testid="calendar-feed-reset-dialog"
        title="Reset your private calendar link?" description="Your current link will stop working immediately and any calendar you subscribed with it will stop updating — you'll need to subscribe again with the new link."
        confirmLabel="Reset link" cancelLabel="Keep current link" />
    </div>
  );
};
